import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { deleteWorkspaceQuizzes } from '../../../api/workspaces/quiz';
import { WorkspaceQuiz } from '../../../api/workspaces/quiz/types';

type DeleteWorkspaceQuizByWorkspaceIdParams = {
  workspaceQuizIds: string[];
  workspaceId: string;
};

type Context = { previousWorkspaceQuizzes: WorkspaceQuiz[] };

function workspaceQuizDeletedUpdater({
  workspaceQuizIdsSet,
}: {
  workspaceQuizIdsSet: Set<string>;
}) {
  return produce((workspaceQuizzes: WorkspaceQuiz[]) =>
    workspaceQuizzes.filter(
      (workspaceQuiz) => !workspaceQuizIdsSet.has(workspaceQuiz.id)
    )
  );
}

export function useDeleteWorkspaceQuizzes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWorkspaceQuizzes,
    onMutate: ({ workspaceQuizIds, workspaceId }): Context => {
      const currentWorkspaceQuizzes = queryClient.getQueryState<
        WorkspaceQuiz[]
      >(workspaceKeys.quizzes({ workspaceId: workspaceId }));
      if (!currentWorkspaceQuizzes.data) {
        return { previousWorkspaceQuizzes: null };
      }

      const workspaceQuizIdsSet = new Set(workspaceQuizIds);

      queryClient.setQueryData<WorkspaceQuiz[]>(
        workspaceKeys.quizzes({ workspaceId: workspaceId }),
        workspaceQuizDeletedUpdater({
          workspaceQuizIdsSet: workspaceQuizIdsSet,
        })
      );

      return { previousWorkspaceQuizzes: currentWorkspaceQuizzes.data };
    },
    onSettled: async (data, error, { workspaceId }) => {
      return Promise.all([
        queryClient.invalidateQueries(['workspaces', workspaceId, 'quizzes']),
        queryClient.invalidateQueries(workspaceKeys.list()),
      ]);
    },
    onError: (
      _error,
      { workspaceId }: DeleteWorkspaceQuizByWorkspaceIdParams,
      { previousWorkspaceQuizzes }: Context
    ) => {
      const allWorkspaceQuizzes = queryClient.getQueryState<WorkspaceQuiz[]>(
        workspaceKeys.quizzes({ workspaceId: workspaceId })
      );

      if (!allWorkspaceQuizzes || !previousWorkspaceQuizzes) return;

      queryClient.setQueryData<WorkspaceQuiz[]>(
        workspaceKeys.quizzes({ workspaceId: workspaceId }),
        previousWorkspaceQuizzes
      );
    },
  });
}
